import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatNaira } from '@/lib/utils';
import { MapPin, Clock, CurrencyCircleDollar, ArrowRight, Funnel, CaretLeft, Briefcase } from '@phosphor-icons/react';

export default function ArtisanFeedPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/jobs/feed/artisan');
        setJobs(data.jobs || []);
      } catch {} finally { setLoading(false); }
    };
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [api]);

  const filtered = filter === 'all' ? jobs : jobs.filter(j => j.category === filter);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] pb-24 max-w-[480px] mx-auto" data-testid="artisan-feed-page">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5 px-4 py-3">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/')} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
          <h1 className="font-heading text-lg font-bold text-[#1C1C1C] flex-1">Job Feed</h1>
          {user && <span className="text-xs font-bold text-[#E86A33] bg-[#E86A33]/10 px-3 py-1 rounded-full">{user.role === 'facility_manager' ? 'All Jobs' : user.trade || 'Artisan'}</span>}
        </div>
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {['all', 'plumbing', 'electrical', 'carpentry', 'painting'].map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filter === f ? 'bg-[#E86A33]/10 border-[#E86A33] text-[#E86A33] border' : 'bg-[#E8DDD2] text-[#5C5C5C] border border-transparent'}`} data-testid={`filter-${f}`}>
              {f === 'all' ? 'All Jobs' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </header>

      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-feed">
            <Briefcase size={48} className="text-[#E5E3DB] mx-auto mb-4" />
            <h3 className="font-heading text-lg font-bold text-[#1C1C1C] mb-2">No jobs available</h3>
            <p className="text-sm text-[#5C5C5C] font-body">New jobs will appear here when customers post them.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(job => (
              <Link key={job.id} to={`/job/${job.id}`} className="block bg-white border border-[#D4C5B3] rounded-xl p-4 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200" data-testid={`feed-job-${job.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 bg-[#1A4D2E]/10 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                    {job.category === 'plumbing' ? '🔧' : job.category === 'electrical' ? '⚡' : job.category === 'carpentry' ? '🪚' : '🔨'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#5C5C5C]">{job.category?.replace(/_/g, ' ')}</p>
                    <h3 className="text-sm font-bold text-[#1C1C1C] truncate font-body">{job.title}</h3>
                    <p className="text-xs text-[#5C5C5C] flex items-center gap-1 mt-1"><MapPin size={12} />{job.lga}, {job.state}</p>
                  </div>
                </div>
                <div className="flex gap-2 mb-3">
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-[#1A4D2E]/5 text-[#1A4D2E]">{job.pool}</span>
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-[#F4A261]/10 text-[#F4A261]"><Clock size={10} className="inline" /> {job.days_needed} day{job.days_needed > 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-[#D4C5B3]">
                  {job.estimate && <span className="font-heading text-sm font-bold text-[#E86A33]">{formatNaira(job.estimate.total_min)} – {formatNaira(job.estimate.total_max)}</span>}
                  {job.my_bid ? (
                    <span className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#2D6A4F]/10 text-[#2D6A4F] border border-[#2D6A4F]/20">Bid Placed</span>
                  ) : (
                    <span className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#E86A33] text-white">Bid Now</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
